import { useMemo, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { UnifiedThemeProvider } from '@backstage/theme';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import GroupIcon from '@material-ui/icons/GroupOutlined';
import { useTranslation } from 'react-i18next';

import { AppShell, TopBar } from '../shell';
import { useProjectStore } from '../projects/useProjectStore';
import { CreateProjectDialog } from '../projects/CreateProjectDialog';
import { CreateKnowledgeBaseDialog } from './dialogs/CreateKnowledgeBaseDialog';
import { ManagePermissionsDialog } from './dialogs/ManagePermissionsDialog';
import { useActiveProject } from '../activeProject/ActiveProjectContext';
import { canSeeOrgAdmin, useCurrentUser } from '../currentUser';
import { SettingsDialog } from '../dialogs/SettingsDialog';
import type { ThemeMode } from '../dialogs/SettingsDialog';
import { darkThemeSystemFont, lightThemeSystemFont } from '../../theme/theme';
import { createDashboardNav } from './dashboardNav';
import { canPerform } from '../../roles';

export function DashboardShell({ children }: { children: React.ReactNode }) {
  const [projectDialog, setProjectDialog] = useState(false);
  const [kbDialog, setKbDialog] = useState(false);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [previewThemeMode, setPreviewThemeMode] = useState<ThemeMode | null>(
    null,
  );
  const appThemeApi = useApi(appThemeApiRef);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const { t: shellT } = useTranslation('shell');
  const { t: projectT } = useTranslation('projects');

  const {
    projects,
    createProjectShell,
    assignMembers,
    updateProject,
    refreshProjects,
  } = useProjectStore();
  const { activeProjectId } = useActiveProject();
  const { status: userStatus, user, currentOrganization } = useCurrentUser();

  const showOrgAdmin = canSeeOrgAdmin(userStatus, currentOrganization);

  // The signed-in user's role on the selected project; nothing is shown when
  // no project is selected, since there is no role to report.
  const activeRole = projects.find(
    project => project.id === String(activeProjectId),
  )?.role;

  const availableProjects = useMemo(
    () => projects.filter(project => !project.deleted),
    [projects],
  );

  const baseNav = useMemo(
    () =>
      createDashboardNav({
        translateShell: shellT,
        translateProjects: projectT,
        projects: availableProjects,
        showOrgAdmin,
      }),
    [availableProjects, shellT, projectT, showOrgAdmin],
  );

  const nav = useMemo(
    () => ({
      ...baseNav,

      entries: baseNav.entries.map(entry =>
        entry.kind === 'group' && entry.id === 'knowledge-bases'
          ? {
              ...entry,
              onCreate: () => setKbDialog(true),
            }
          : entry,
      ),
    }),
    [baseNav],
  );

  const handleProjectDialogClose = () => {
    setProjectDialog(false);

    /*
     * Refresh the project selector after the project
     * creation dialog closes.
     */
    void refreshProjects();
  };

  const handleSettingsClose = () => {
    setSettingsDialog(false);
  };

  // Backup for the normal case (the Dialog's close transition completes
  // uninterrupted). Not relied on as the only reset path - see
  // SettingsDialog's onThemePreviewReset for why.
  const handleSettingsExited = () => {
    setPreviewThemeMode(null);
  };

  const handleThemePreviewReset = () => {
    setPreviewThemeMode(null);
  };

  const activeThemeId = appThemeApi.getActiveThemeId();
  const committedThemeMode: ThemeMode =
    activeThemeId === 'light' || activeThemeId === 'dark'
      ? activeThemeId
      : 'auto';
  const visibleThemeMode = previewThemeMode ?? committedThemeMode;
  const previewTheme =
    visibleThemeMode === 'dark' || (visibleThemeMode === 'auto' && prefersDark)
      ? darkThemeSystemFont
      : lightThemeSystemFont;

  return (
    <UnifiedThemeProvider theme={previewTheme}>
      <>
        <AppShell
          nav={nav}
          topbar={
            <TopBar
              onSettings={() => setSettingsDialog(true)}
              roleLabel={activeRole && projectT(`roles.${activeRole}`)}
              user={user ?? undefined}
            />
          }
          sidebarProps={{
            onCreate: () => setProjectDialog(true),

            // Searching users and saving roles need invite rights; showing the
            // dialog to Viewers and Collaborators only leads to 403s.
            footer: canPerform(activeRole, 'project.invite')
              ? {
                  icon: GroupIcon,
                  label: shellT('managePermissions'),
                  onClick: () => setPermissionsDialog(true),
                }
              : undefined,
          }}
        >
          {children}
        </AppShell>

        <CreateProjectDialog
          open={projectDialog}
          onClose={handleProjectDialogClose}
          onCreateProject={createProjectShell}
          onUpdateProject={updateProject}
          onAssignMembers={assignMembers}
        />

        <CreateKnowledgeBaseDialog
          open={kbDialog}
          onClose={() => setKbDialog(false)}
        />

        <ManagePermissionsDialog
          open={permissionsDialog}
          onClose={() => setPermissionsDialog(false)}
          /*
           * A saved role change (an ownership transfer above all) changes
           * the signed-in user's own role, so reload the projects the header
           * and the role-gated actions read it from.
           */
          onSaved={() => void refreshProjects()}
          canTransferOwnership={canPerform(
            activeRole,
            'project.ownership-transfer',
          )}
        />

        <SettingsDialog
          open={settingsDialog}
          onClose={handleSettingsClose}
          onExited={handleSettingsExited}
          onThemePreview={setPreviewThemeMode}
          onThemePreviewReset={handleThemePreviewReset}
        />
      </>
    </UnifiedThemeProvider>
  );
}
